import { useQuery } from "@tanstack/react-query";
import {
  MapboxSuggestion,
  MapboxSuggestionsResponse,
  Suggestion,
  suggestionKeys,
  SuggestionsResponse,
} from "~/features/suggestions";

import { useLocalContent } from "~/sdks/LocalContent/context";
import useDebounce from "~/hooks/useDebounce";

const MAPBOX_ENDPOINT = "https://api.mapbox.com/search/searchbox/v1/forward?";

const formatSuggestion = (suggestion: MapboxSuggestion): Suggestion => {
  const { properties } = suggestion;
  const { context } = suggestion.properties;

  return {
    label: properties.name,
    language: properties.language,
    countryCode: context.country?.country_code_alpha_3 ?? "",
    locationId: properties.mapbox_id,
    address: {
      description: `${properties.name}, ${properties.place_formatted}`,
      country: context.country?.name ?? "",
      state: context.region?.region_code ?? "",
      city: context.place?.name ?? "",
      postalCode: context.postcode?.name ?? "",
      county: context.district?.name ?? "",
      street: context.address?.street_name ?? "",
      houseNumber: context.address?.address_number ?? "",
      coordinates: {
        latitude: properties.coordinates.latitude,
        longitude: properties.coordinates.longitude,
      },
    },
  };
};

const transformSuggestionsResponse = (
  response: MapboxSuggestionsResponse
): SuggestionsResponse => ({
  suggestions: response.features.map((s: MapboxSuggestion) =>
    formatSuggestion(s)
  ),
});

export function useAddressSearch({ query }: { query: string }) {
  const { location, country, locale } = useLocalContent();
  const debouncedQuery = useDebounce(query, 1250);

  const options = {
    q: debouncedQuery,
    language: locale,
    proximity: `${location.lng},${location.lat}`,
    origin: `${location.lng},${location.lat}`,
    country,
    types: "poi,address,street,neighborhood,locality,city,place",
    limit: "5",
    access_token: import.meta.env.VITE_MAPBOX_API_KEY as string,
  };

  const fetchSuggestions = async (): Promise<SuggestionsResponse> => {
    const response = await fetch(
      MAPBOX_ENDPOINT + new URLSearchParams(options)
    );

    return transformSuggestionsResponse(await response.json());
  };

  return useQuery({
    queryKey: suggestionKeys.list(options),
    queryFn: fetchSuggestions,
    enabled: !!debouncedQuery.length && debouncedQuery.length >= 4,
  });
}
